import { getContactCustomerTypes } from "../../../../@types/sharedSchema";
import type { RealEstateContact } from "../../../../@types/sharedSchema";
import type { CrmContactRow } from "../../../../@types/webotServer";

/**
 * A `RealEstateContact` (the Firestore document) projected onto the row shape the list
 * renders (`CrmContactRow`, 27 flat fields out of Postgres).
 *
 * WHY THIS EXISTS. The list is served from the Postgres mirror; the panel reads the
 * Firestore document live. Edit a contact in the panel — rename it, set a status, fill a
 * budget — and the document changes immediately while the mirror is up to a drain tick
 * behind. Without this the row beside the open panel keeps showing the OLD name next to
 * the new one, which reads as a save that did not take.
 *
 * So the panel projects its live document back onto the row and hands it to the list's
 * own patch channel. It is not a cache and not a second source of truth: the next real
 * fetch overwrites it wholesale, and every field here is derived from the same document
 * the mirror will be rebuilt from.
 *
 * DERIVE, DON'T READ, THE FLATTENED ARRAYS. `interestedInCitiesDistinct` and
 * `interestedInNeighborhoodsDistinct` are stamped by the `flattenInterestedInLocations`
 * Cloud Function, so they are STALE for the seconds after an edit — reading them would
 * make the row lag the panel in exactly the moment this function exists to cover.
 * `interestedInLocations` is what the user actually edited, so the cities come from there.
 */

const distinct = (values: (string | null | undefined)[]): string[] => [
  ...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))
];

/**
 * The mirror stores numbers, but 338 of 2,374 budgeted contacts hold `customerBudget` as
 * a numeric STRING — so the row would get a string where it expects a number and
 * `formatBudget` would render it unformatted. Same coercion the server's
 * `asNumberOrNull` applies on the write path.
 */
const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(String(value).replace(/[,\s₪]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

export function projectContactOntoRow(contact: RealEstateContact): Partial<CrmContactRow> {
  const locations = Array.isArray(contact.interestedInLocations) ? contact.interestedInLocations : [];

  const firstName = toStringOrNull(contact.callerFirstName);
  const lastName = toStringOrNull(contact.callerLastName);

  return {
    // `contactFullName` is Cloud-Function-derived like the arrays above, so the name is
    // rebuilt from its two inputs for the same reason.
    name: distinct([firstName, lastName]).join(" ") || null,
    firstName,
    lastName,
    additionalNotes: toStringOrNull(contact.additionalNotes),
    contactOrigin: toStringOrNull(contact.contactOrigin),

    crmStatus: toStringOrNull(contact.crmStatus),
    crmSubStatus: toStringOrNull(contact.crmSubStatus),
    crmIsHot: Boolean(contact.crmIsHot),
    customerSeriousness: toStringOrNull(contact.customerSeriousness),

    // THROUGH THE SHARED READER, never `contact.customerTypes` alone. `customerTypes` is
    // the canonical array and `customerType` a legacy scalar mirror, and old documents
    // may carry ONLY the scalar — which is why sharedSchema states that every read goes
    // through this helper and why no backfill was ever done.
    //
    // Reading the array directly made a legacy-only contact flip from "has a type" to
    // "missing a type" the moment anything edited it through the panel: the mirror's
    // `customer_types` column and the server's emptiness rule both use the union, so the
    // row would disagree with the very filter that surfaced it.
    customerTypes: distinct(getContactCustomerTypes(contact)),
    tags: Array.isArray(contact.tags) ? distinct(contact.tags) : [],
    cities: distinct(locations.map((location) => location?.city)),
    neighborhoods: distinct(locations.map((location) => location?.neighborhood)),
    propertyTypes: Array.isArray(contact.interestedInPropertyTypes)
      ? distinct(contact.interestedInPropertyTypes)
      : [],

    // The seller lens's seeds. Written directly by the edit dialog's פרופיל מוכר/משכיר
    // pickers (no Cloud Function derives them), so the document value is current the
    // moment the form saves and projecting it keeps the lens's listing cell in step
    // with an edit made in the panel.
    sellingListingIds: Array.isArray(contact.sellingListingIds) ? distinct(contact.sellingListingIds) : [],
    rentingOutListingIds: Array.isArray(contact.rentingOutListingIds)
      ? distinct(contact.rentingOutListingIds)
      : [],

    budget: toNumberOrNull(contact.customerBudget),
    budgetMin: toNumberOrNull(contact.customerBudgetMin),
    budgetRent: toNumberOrNull(contact.customerBudgetRent),
    roomsMin: toNumberOrNull(contact.roomsMin),
    roomsMax: toNumberOrNull(contact.roomsMax)

    // `updatedTime` is deliberately NOT projected. It is stamped asynchronously by the
    // metadata Cloud Function and suppressed entirely by `skipUpdatedTimeAroundDate` on
    // ~99% of contacts, so anything the client invented here would be a worse answer than
    // the mirror's — and the row LABELS this value as a record update, so a fabricated
    // "just now" would be a visible lie on every edit.
  };
}
